import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { GRADIENTS } from './theme';

/**
 * SmartImage — primitive image unique de la lib UI (§11/§18).
 *
 * Enrichit `<img>` avec, par défaut :
 *   - `loading="lazy"`   → l'image ne se charge qu'en approchant du viewport (perf)
 *   - `decoding="async"` → décodage hors thread principal (évite le jank)
 *   - `onError` fallback gracieux → bascule sur `fallback` si la source échoue
 *   - **squelette gris animé** (§39 Phase C) → shimmer tant que l'image n'est pas
 *     chargée, retiré au `onLoad`. Pur CSS sur l'élément `<img>` via `data-loading` :
 *     AUCUN wrapper → structure DOM identique à un `<img>` (zéro régression layout ;
 *     les sélecteurs `img` des cartes — aspect-ratio/object-fit — s'appliquent).
 *
 * Préserve tous les attributs d'un `<img>` classique (className, style, onClick,
 * onLoad, width, height, …) via `...rest`.
 *
 * @param {string}  src        - URL de l'image
 * @param {string}  [alt='']   - texte alternatif
 * @param {string}  [fallback] - URL de repli si `src` échoue (optionnel)
 * @param {boolean} [skeleton=true] - affiche le shimmer pendant le chargement
 */
const shimmer = keyframes`
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const Img = styled.img`
  /* Shimmer UNIQUEMENT pendant le chargement (data-loading=true) — retiré dès
     l'image affichée. N'affecte que le fond (sous l'image) : aucun impact visuel
     sur une image opaque chargée. */
  &[data-loading='true'] {
    background-image: ${GRADIENTS.skeleton};
    background-size: 400% 100%;
    background-repeat: no-repeat;
    animation: ${shimmer} 1.4s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-loading='true'] {
      animation: none;
    }
  }
`;

const SmartImage = ({
  src,
  alt = '',
  fallback = null,
  loading = 'lazy',
  decoding = 'async',
  skeleton = true,
  onError,
  onLoad,
  srcSet,
  ...rest
}) => {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Réinitialise les états si la source change (réutilisation en liste).
  useEffect(() => {
    setErrored(false);
    setLoaded(false);
  }, [src]);

  const handleError = (e) => {
    if (fallback && !errored) setErrored(true);
    if (onError) onError(e);
  };

  const handleLoad = (e) => {
    setLoaded(true);
    if (onLoad) onLoad(e);
  };

  return (
    <Img
      src={errored && fallback ? fallback : src}
      srcSet={errored ? undefined : srcSet}
      alt={alt}
      loading={loading}
      decoding={decoding}
      data-loading={skeleton && !loaded && !errored ? 'true' : 'false'}
      onError={handleError}
      onLoad={handleLoad}
      {...rest}
    />
  );
};

export default SmartImage;
